import { set } from 'dot-object';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { Member } from '../../../../api/ceremony';
import { VowsAnalysisResponse } from '../../../../api/vowsAnalysis';
import Button from '../../../../components/v2/Button/Button';
import { Modal } from '../../../../components/v2/Modal/Modal';
import { Typography } from '../../../../components/v2/Typography/Typography';
import { getFirstName } from '../../../../helpers/nameHelper';


const VowsAnalysisModal = ({
  isOpen,
  onClose,
  vowsAnalysis,
  currentUser,
  onAnalyzeAgain,
}: {
  isOpen: boolean;
  onClose: () => void;
  vowsAnalysis?: VowsAnalysisResponse;
  currentUser?: Member;
  onAnalyzeAgain: () => void;
}) => {
  const [userSuggestions, setUserSuggestions] = React.useState<string[]>([]);
  const [lengthSuggestion, setLengthSuggestion] = React.useState<string>('');
  const [isCooldown, setIsCooldown] = React.useState<boolean>(false);
  const [hoursLeft, setHoursLeft] = React.useState<number>(0);

  const navigate = useNavigate();

  React.useEffect(() => {
    if (!vowsAnalysis) return;
    if (!currentUser) return;

    findUserSuggestions();
    getToneSuggestion();
    handleIsCooldown();
  }, [currentUser, vowsAnalysis, isOpen]);

  const findUserSuggestions = () => {
    if (!currentUser) return;
    if (!vowsAnalysis) return;

    const currentUserId = currentUser.id;

    const isPartner1 = vowsAnalysis?.data?.member_one_id === currentUserId;

    if (isPartner1) {
      setUserSuggestions(
        vowsAnalysis?.data?.vows_analysis?.suggestions.partner1
      );
      return;
    }

    setUserSuggestions(vowsAnalysis?.data?.vows_analysis?.suggestions.partner2);
  };

  const handleIsCooldown = () => {
    const isMoreThan1Hours = vowsAnalysis?.data?.created_at
      ? new Date().getTime() -
          new Date(vowsAnalysis.data.created_at).getTime() >
        1 * 60 * 60 * 1000
      : false;

    if (isMoreThan1Hours) {
      setIsCooldown(false);
      return;
    }

    const minutesLeft = vowsAnalysis?.data?.created_at
      ? 1 * 60 - Math.floor((new Date().getTime() - new Date(vowsAnalysis.data.created_at).getTime()) / 1000 / 60)
      : 0;

    setHoursLeft(minutesLeft);
    setIsCooldown(true);
  };

  const getToneSuggestion = () => {
    if (!vowsAnalysis) return;

    const length1 = vowsAnalysis?.data?.vows_analysis?.length.couple1;
    const length2 = vowsAnalysis?.data?.vows_analysis?.length.couple2;

    const absoluteDifference = Math.abs(length1 - length2);

    switch (absoluteDifference) {
      case 0:
        setLengthSuggestion(
          "Your vows are perfectly balanced! Consider rehearsing until you're satisfied with your delivery."
        );
        break;
      case 1:
        setLengthSuggestion(
          "Your vows are balanced! Consider rehearsing until you're satisfied with your delivery."
        );
        break;
      case 2:
        setLengthSuggestion(
          'Your vows are slightly unbalanced! Consider making them more similar in length.'
        );
        break;
      case 3:
        setLengthSuggestion(
          'Your vows are unbalanced! Consider making them more similar in length.'
        );
        break;
      default:
        setLengthSuggestion(
          'The difference in length is obvious! Consider making them more similar in length.'
        );
        break;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <>
        <div className='text-center flex flex-col gap-2'>
          <Typography type='display-100'>Comparative Vow Analysis</Typography>

          <Typography type='body-600'>
            <>
              We have analyzed your vows and your partner's vows. <br />
              This is what we found.
            </>
          </Typography>
        </div>
        <ToneCard
          toneText1={vowsAnalysis?.data?.vows_analysis?.tone?.couple1 || ''}
          toneText2={vowsAnalysis?.data?.vows_analysis?.tone?.couple2 || ''}
        />
        <LengthCard
          partner1Minutes={
            vowsAnalysis?.data?.vows_analysis?.length?.couple1.toString() || ''
          }
          partner1FirstName={
            getFirstName(vowsAnalysis?.data?.memberOne.legal_name) || ''
          }
          partner2Minutes={
            vowsAnalysis?.data?.vows_analysis?.length?.couple2.toString() || ''
          }
          partner2FirstName={
            getFirstName(vowsAnalysis?.data?.memberTwo.legal_name) || ''
          }
          text={lengthSuggestion || ''}
        />
        <SuggestionsCard suggestions={userSuggestions || []} />
        {isCooldown && (
          <div className='flex flex-col gap-2 mt-4 text-center'>
            <Typography type='body-400' className='text-forest-500'>
              <>
                You can analyze your vows again in {hoursLeft.toString()} minutes.
                This should give you and your partner enough time to improve
                your scripts.
              </>
            </Typography>
          </div>
        )}
        <div className='flex flex-row justify-center items-center gap-4 pb-20 md:pb-0'>
          <Button
            onClick={onAnalyzeAgain}
            variant={isCooldown ? 'primary' : 'secondary'}
            className='px-12 self-center mt-8'
            disabled={isCooldown}
          >
            Analyze Again
          </Button>
          <Button
            onClick={() => {
              navigate(`/vow-builder-ai/${vowsAnalysis?.data?.ceremony_id || ''}`);
              onClose();
            }}
            variant='primary'
            className='px-12 self-center mt-8'
          >
            Edit Vows
          </Button>
        </div>
      </>
    </Modal>
  );
};

const Card = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className='flex flex-col gap-4 mt-8 border border-forest-100 rounded-xl p-8'>
      {children}
    </div>
  );
};

const ToneCard = ({
  toneText1,
  toneText2,
}: {
  toneText1: string;
  toneText2: string;
}) => {
  return (
    <Card>
      <Typography type='display-100'>Tone</Typography>
      <div className='flex flex-col gap-2'>
        <div className='flex items-start gap-2'>
          <Typography type='body-600'>•</Typography>
          <Typography type='body-600'>{toneText1}</Typography>
        </div>
        <div className='flex items-start gap-2'>
          <Typography type='body-600'>•</Typography>
          <Typography type='body-600'>{toneText2}</Typography>
        </div>
      </div>
    </Card>
  );
};

const LengthCard = ({
  partner1Minutes,
  partner1FirstName,
  partner2Minutes,
  partner2FirstName,
  text,
}: {
  partner1Minutes: string;
  partner1FirstName: string;
  partner2Minutes: string;
  partner2FirstName: string;
  text: string;
}) => {
  return (
    <Card>
      <Typography type='display-100'>Length</Typography>
      <div className='flex flex-col gap-2'>
        <div className='flex flex-col  md:flex-row justify-evenly items-center gap-2'>
          <div className='flex items-start w-full md:w-auto gap-2 bg-cosmicLatte-dark px-16 py-8 rounded-xl text-center'>
            <div className='flex flex-col gap-2 w-full'>
              <div>
                <Typography type='display-50'>
                  <>{partner1Minutes} mins</>
                </Typography>
              </div>

              <div>
                <Typography type='body-600'>
                  <>{partner1FirstName}’s vows</>
                </Typography>
              </div>
            </div>
          </div>
          <div className='flex items-start w-full md:w-auto gap-2 bg-cosmicLatte-dark px-16 py-8 rounded-xl text-center'>
            <div className='flex flex-col gap-2 w-full'>
              <div>
                <Typography type='display-50'>
                  <>{partner2Minutes} mins</>
                </Typography>
              </div>

              <div>
                <Typography type='body-600'>
                  <>{partner2FirstName}’s vows</>
                </Typography>
              </div>
            </div>
          </div>
        </div>
        <div className='flex items-start gap-2'>
          <Typography type='body-600'>•</Typography>
          <Typography type='body-600'>{text}</Typography>
        </div>
      </div>
    </Card>
  );
};

const SuggestionsCard = ({ suggestions }: { suggestions: string[] }) => {
  return (
    <Card>
      <Typography type='display-100'>Your Suggestions</Typography>
      <div className='flex flex-col gap-2'>
        {suggestions.map((suggestion, index) => (
          <div key={index} className='flex items-center gap-2'>
            <div className='bg-forest-100 rounded-full w-8 h-8 flex self-start items-center justify-center  flex-shrink-0'>
              <Typography type='body-600'>{`${index + 1}`}</Typography>
            </div>
            <Typography type='body-600'>{suggestion}</Typography>
          </div>
        ))}
      </div>
      <Typography type='body-400' className='text-forest-400'>
        Note that these suggestions are subjective, aiming to subtly balance
        couples' vows while preserving uniqueness and authenticity. With each
        new analysis, new suggestions will be identified. It is up to you to
        decide when it's time to stop analyzing. Your heart will tell you when
        your vows are ready!
      </Typography>
    </Card>
  );
};

export default VowsAnalysisModal;
